import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  HelpCircle, Plus, Edit2, Trash2, Eye, EyeOff, Save, X, 
  RefreshCw, ArrowUp, ArrowDown, AlertCircle, Database
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionsEditor = ({ token }) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Fehler beim Laden der Fragen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (question) => {
    try {
      await axios.put(
        `${API}/admin/questions/${question.id}`,
        {
          question_de: question.question_de,
          question_en: question.question_en,
          question_es: question.question_es,
          options: question.options,
          section: question.section,
          order: question.order,
          required: question.required,
          weight: question.weight || 0,
          active: question.active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Frage aktualisiert');
      setEditingQuestion(null);
      await loadQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleToggleActive = async (questionId, currentStatus) => {
    try {
      await axios.post(
        `${API}/admin/questions/${questionId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(currentStatus ? 'Frage deaktiviert' : 'Frage aktiviert');
      await loadQuestions();
    } catch (error) {
      console.error('Error toggling question:', error);
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Frage wirklich löschen? Dies kann die Quiz-Funktionalität beeinträchtigen!')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Frage gelöscht');
      await loadQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const getSectionLabel = (section) => {
    const labels = {
      'destination': '🎯 Zielgebietslogik (Q1-Q8)',
      'experience': '✨ Erlebnisprofil (Q9-Q15)',
      'basic': '📋 Basis-Informationen',
      'preferences': '🎯 Präferenzen'
    };
    return labels[section] || section;
  };

  if (isLoading) {
    return (
      <div className="questions-editor-loading">
        <RefreshCw className="spin" size={48} />
        <p>Lade Fragen...</p>
      </div>
    );
  }

  return (
    <div className="questions-editor">
      <div className="questions-editor-header">
        <div>
          <h2>Fragen-Management</h2>
          <p>Verwalte Quiz-Fragen und Antwortoptionen</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={async () => {
              if (!window.confirm('Möchten Sie die Standard-Fragen mit korrekten Gewichtungen neu laden? Dies überschreibt alle vorhandenen Fragen!')) return;
              setIsInitializing(true);
              try {
                const response = await axios.post(`${API}/admin/questions/init`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(`Fragen erfolgreich neu geladen! ${response.data.count || 15} Fragen mit Gewichtungen initialisiert.`);
                await loadQuestions();
              } catch (error) {
                console.error('Error initializing questions:', error);
                toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
              } finally {
                setIsInitializing(false);
              }
            }}
            disabled={isInitializing}
            style={{
              padding: '10px 16px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isInitializing ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RefreshCw size={18} className={isInitializing ? 'spin' : ''} />
            {isInitializing ? 'Wird geladen...' : 'Standard-Fragen laden'}
          </button>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={20} />
            Neue Frage hinzufügen
          </button>
        </div>
      </div>

      <div className="questions-info-banner">
        <AlertCircle size={20} />
        <div>
          <strong>Wichtig:</strong> Änderungen an Fragen werden sofort aktiv. 
          Stelle sicher, dass der Frontend-Code die Frage-IDs korrekt verwendet.
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="questions-empty" style={{ padding: '60px 40px', textAlign: 'center' }}>
          <Database size={64} style={{ color: '#c084fc', marginBottom: '20px' }} />
          <h3 style={{ marginBottom: '12px', color: '#374151' }}>Keine Fragen vorhanden</h3>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Die Fragen-Datenbank ist leer. Klicken Sie auf den Button oben, um die Standard-Fragen zu initialisieren.
          </p>
        </div>
      ) : (
        <div className="questions-list">
          {['destination', 'experience', 'basic', 'preferences'].map(section => {
            const sectionQuestions = questions.filter(q => q.section === section);
            if (sectionQuestions.length === 0) return null;

            return (
              <div key={section} className="questions-section">
                <h3>{getSectionLabel(section)}</h3>
                
                <div className="questions-items">
                  {sectionQuestions.map((question) => (
                    <div key={question.id} className={`question-item ${!question.active ? 'inactive' : ''}`}>
                      <div className="question-item-header">
                        <div className="question-item-title">
                          <span className="question-id">{question.id}</span>
                          <span className="question-order">#{question.order}</span>
                          {question.section === 'destination' && (
                            <span style={{ 
                              backgroundColor: '#fef3c7', 
                              color: '#92400e', 
                              padding: '2px 8px', 
                              borderRadius: '4px', 
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              ⚖️ {question.weight || 0}
                            </span>
                          )}
                          {!question.active && (
                            <span className="inactive-badge">Inaktiv</span>
                          )}
                        </div>
                        
                        <div className="question-item-actions">
                          <button
                            className="btn-icon"
                            onClick={() => handleToggleActive(question.id, question.active)}
                            title={question.active ? 'Deaktivieren' : 'Aktivieren'}
                          >
                            {question.active ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => setEditingQuestion({...question})}
                            title="Bearbeiten"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleDelete(question.id)}
                            title="Löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {editingQuestion?.id === question.id ? (
                        <EditQuestionForm
                          question={editingQuestion}
                          onChange={setEditingQuestion}
                          onSave={handleUpdate}
                          onCancel={() => setEditingQuestion(null)}
                        />
                      ) : (
                        <div className="question-preview">
                          <div className="question-text">
                            <strong>🇩🇪</strong> {question.question_de}
                          </div>
                          <div className="question-text">
                            <strong>🇬🇧</strong> {question.question_en}
                          </div>
                          <div className="question-text">
                            <strong>🇪🇸</strong> {question.question_es}
                          </div>
                          
                          <div className="question-options-preview">
                            <strong>{question.options.length} Optionen:</strong>
                            {question.options.slice(0, 3).map((opt, i) => (
                              <span key={i} className="option-badge">{opt.label_de}</span>
                            ))}
                            {question.options.length > 3 && (
                              <span className="option-badge">+{question.options.length - 3} mehr</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddQuestionModal
          onClose={() => setShowAddModal(false)}
          onCreate={async (newQuestion) => {
            try {
              await axios.post(
                `${API}/admin/questions`,
                newQuestion,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success('Frage erstellt');
              setShowAddModal(false);
              await loadQuestions();
            } catch (error) {
              console.error('Error creating question:', error);
              toast.error('Fehler beim Erstellen');
            }
          }}
          existingQuestions={questions}
        />
      )}
    </div>
  );
};

const EditQuestionForm = ({ question, onChange, onSave, onCancel }) => {
  const [newOption, setNewOption] = useState({
    value: '',
    label_de: '',
    label_en: '',
    label_es: ''
  });

  const addOption = () => {
    if (!newOption.value || !newOption.label_de) {
      toast.error('Bitte mindestens Value und deutsches Label angeben');
      return;
    }
    
    const optionToAdd = {
      value: newOption.value,
      label_de: newOption.label_de,
      label_en: newOption.label_en || newOption.label_de,
      label_es: newOption.label_es || newOption.label_de
    };
    
    onChange({
      ...question,
      options: [...question.options, optionToAdd]
    });
    
    setNewOption({ value: '', label_de: '', label_en: '', label_es: '' });
  };

  const removeOption = (index) => {
    onChange({
      ...question,
      options: question.options.filter((_, i) => i !== index)
    });
  };

  const updateOption = (index, field, value) => {
    const updatedOptions = [...question.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value
    };
    onChange({
      ...question,
      options: updatedOptions
    });
  };

  return (
    <div className="edit-question-form">
      <div className="form-group">
        <label>🇩🇪 Frage (Deutsch)</label>
        <input
          type="text"
          value={question.question_de}
          onChange={(e) => onChange({...question, question_de: e.target.value})}
        />
      </div>

      <div className="form-group">
        <label>🇬🇧 Question (English)</label>
        <input
          type="text"
          value={question.question_en}
          onChange={(e) => onChange({...question, question_en: e.target.value})}
        />
      </div>

      <div className="form-group">
        <label>🇪🇸 Pregunta (Español)</label>
        <input
          type="text"
          value={question.question_es}
          onChange={(e) => onChange({...question, question_es: e.target.value})}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Section</label>
          <select
            value={question.section}
            onChange={(e) => onChange({...question, section: e.target.value})}
          >
            <option value="destination">🎯 Zielgebietslogik (wirkt auf Algorithmus)</option>
            <option value="experience">✨ Erlebnisprofil (nur Info)</option>
            <option value="basic">Basic</option>
            <option value="preferences">Preferences</option>
          </select>
        </div>

        <div className="form-group">
          <label>Reihenfolge</label>
          <input
            type="number"
            value={question.order}
            onChange={(e) => onChange({...question, order: parseInt(e.target.value)})}
          />
        </div>
      </div>

      {question.section === 'destination' && (
        <div style={{ 
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fcd34d'
        }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '16px' }}>⚖️</span>
              <strong>Algorithmus-Gewichtung (0-100)</strong>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={question.weight || 0}
                onChange={(e) => onChange({...question, weight: parseInt(e.target.value)})}
                style={{ flex: 1 }}
              />
              <input
                type="number"
                min="0"
                max="100"
                value={question.weight || 0}
                onChange={(e) => onChange({...question, weight: parseInt(e.target.value) || 0})}
                style={{ width: '70px', padding: '6px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
              />
            </div>
            <p style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
              Höhere Werte = stärkerer Einfluss auf die Destination-Auswahl
            </p>
          </div>
        </div>
      )}

      <div className="form-group" style={{ marginTop: '12px' }}>
        <label>
          <input
            type="checkbox"
            checked={question.required}
            onChange={(e) => onChange({...question, required: e.target.checked})}
          />
          Pflichtfrage
        </label>
      </div>

      <div style={{ 
        marginTop: '20px', 
        padding: '16px', 
        backgroundColor: '#f9fafb', 
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Antwortoptionen bearbeiten ({question.options.length})
        </h4>

        {question.options.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            {question.options.map((opt, index) => (
              <div key={index} style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '6px',
                marginBottom: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#7c3aed' }}>Option {index + 1}</span>
                  <button 
                    type="button"
                    onClick={() => removeOption(index)}
                    style={{
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      color: '#dc2626'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Value"
                    value={opt.value}
                    onChange={(e) => updateOption(index, 'value', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    placeholder="🇩🇪 Label Deutsch"
                    value={opt.label_de}
                    onChange={(e) => updateOption(index, 'label_de', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="🇬🇧 Label English"
                    value={opt.label_en}
                    onChange={(e) => updateOption(index, 'label_en', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    placeholder="🇪🇸 Label Español"
                    value={opt.label_es}
                    onChange={(e) => updateOption(index, 'label_es', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ 
          padding: '12px',
          backgroundColor: 'white',
          borderRadius: '6px',
          border: '1px dashed #c084fc'
        }}>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Neue Option hinzufügen:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder="Value (intern)"
              value={newOption.value}
              onChange={(e) => setNewOption({...newOption, value: e.target.value})}
              style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
            />
            <input
              type="text"
              placeholder="🇩🇪 Label Deutsch"
              value={newOption.label_de}
              onChange={(e) => setNewOption({...newOption, label_de: e.target.value})}
              style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder="🇬🇧 Label English (optional)"
              value={newOption.label_en}
              onChange={(e) => setNewOption({...newOption, label_en: e.target.value})}
              style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
            />
            <input
              type="text"
              placeholder="🇪🇸 Label Español (optional)"
              value={newOption.label_es}
              onChange={(e) => setNewOption({...newOption, label_es: e.target.value})}
              style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
            />
          </div>
          <button
            type="button"
            onClick={addOption}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#c084fc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
          >
            <Plus size={16} />
            Option hinzufügen
          </button>
        </div>
      </div>

      <div className="edit-form-actions" style={{ marginTop: '20px' }}>
        <button className="btn-secondary" onClick={onCancel}>
          <X size={16} />
          Abbrechen
        </button>
        <button className="btn-primary" onClick={() => onSave(question)}>
          <Save size={16} />
          Speichern
        </button>
      </div>
    </div>
  );
};

const AddQuestionModal = ({ onClose, onCreate, existingQuestions }) => {
  const nextOrder = Math.max(...existingQuestions.map(q => q.order || 0), 0) + 1;
  
  const [newQuestion, setNewQuestion] = useState({
    id: `q${nextOrder}`,
    order: nextOrder,
    section: 'destination',
    question_de: '',
    question_en: '',
    question_es: '',
    options: [],
    active: true,
    required: true,
    weight: 20
  });

  const [newOption, setNewOption] = useState({
    value: '',
    label_de: '',
    label_en: '',
    label_es: ''
  });

  const addOption = () => {
    if (!newOption.value || !newOption.label_de) {
      toast.error('Bitte mindestens Value und deutsches Label angeben');
      return;
    }
    
    const optionToAdd = {
      value: newOption.value,
      label_de: newOption.label_de,
      label_en: newOption.label_en || newOption.label_de,
      label_es: newOption.label_es || newOption.label_de
    };
    
    setNewQuestion({
      ...newQuestion,
      options: [...newQuestion.options, optionToAdd]
    });
    
    setNewOption({ value: '', label_de: '', label_en: '', label_es: '' });
  };

  const removeOption = (index) => {
    setNewQuestion({
      ...newQuestion,
      options: newQuestion.options.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (newQuestion.options.length === 0) {
      toast.error('Bitte mindestens eine Antwortoption hinzufügen');
      return;
    }
    
    onCreate(newQuestion);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflow: 'auto' }}>
        <h3>Neue Frage erstellen</h3>
        <p className="modal-subtitle">
          Erstellen Sie eine neue Frage mit allen Übersetzungen und Antwortoptionen.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Frage-ID (z.B. q16)</label>
              <input
                type="text"
                value={newQuestion.id}
                onChange={(e) => setNewQuestion({...newQuestion, id: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Sektion</label>
              <select
                value={newQuestion.section}
                onChange={(e) => setNewQuestion({...newQuestion, section: e.target.value})}
              >
                <option value="destination">🎯 Zielgebietslogik (wirkt auf Algorithmus)</option>
                <option value="experience">✨ Erlebnisprofil (nur Info)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>🇩🇪 Frage (Deutsch)</label>
            <input
              type="text"
              value={newQuestion.question_de}
              onChange={(e) => setNewQuestion({...newQuestion, question_de: e.target.value})}
              required
              placeholder="Welche Frage möchten Sie stellen?"
            />
          </div>

          <div className="form-group">
            <label>🇬🇧 Question (English)</label>
            <input
              type="text"
              value={newQuestion.question_en}
              onChange={(e) => setNewQuestion({...newQuestion, question_en: e.target.value})}
              required
              placeholder="What question would you like to ask?"
            />
          </div>

          <div className="form-group">
            <label>🇪🇸 Pregunta (Español)</label>
            <input
              type="text"
              value={newQuestion.question_es}
              onChange={(e) => setNewQuestion({...newQuestion, question_es: e.target.value})}
              required
              placeholder="¿Qué pregunta te gustaría hacer?"
            />
          </div>

          {newQuestion.section === 'destination' && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #fcd34d'
            }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚖️</span>
                  <strong>Algorithmus-Gewichtung (0-100)</strong>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={newQuestion.weight || 0}
                    onChange={(e) => setNewQuestion({...newQuestion, weight: parseInt(e.target.value)})}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newQuestion.weight || 0}
                    onChange={(e) => setNewQuestion({...newQuestion, weight: parseInt(e.target.value) || 0})}
                    style={{ width: '70px', padding: '6px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
                <p style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
                  Höhere Werte = stärkerer Einfluss auf die Destination-Auswahl
                </p>
              </div>
            </div>
          )}

          <div style={{ 
            marginTop: '20px', 
            padding: '16px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              Antwortoptionen ({newQuestion.options.length})
            </h4>

            {newQuestion.options.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                {newQuestion.options.map((opt, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <span style={{ 
                      fontWeight: '600', 
                      color: '#7c3aed',
                      minWidth: '80px'
                    }}>
                      {opt.value}
                    </span>
                    <span style={{ flex: 1 }}>
                      🇩🇪 {opt.label_de} | 🇬🇧 {opt.label_en} | 🇪🇸 {opt.label_es}
                    </span>
                    <button 
                      type="button"
                      onClick={() => removeOption(index)}
                      style={{
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        color: '#dc2626'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ 
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px dashed #c084fc'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Value (intern)"
                  value={newOption.value}
                  onChange={(e) => setNewOption({...newOption, value: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                />
                <input
                  type="text"
                  placeholder="🇩🇪 Label Deutsch"
                  value={newOption.label_de}
                  onChange={(e) => setNewOption({...newOption, label_de: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="🇬🇧 Label English (optional)"
                  value={newOption.label_en}
                  onChange={(e) => setNewOption({...newOption, label_en: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                />
                <input
                  type="text"
                  placeholder="🇪🇸 Label Español (optional)"
                  value={newOption.label_es}
                  onChange={(e) => setNewOption({...newOption, label_es: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                />
              </div>
              <button
                type="button"
                onClick={addOption}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#c084fc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
              >
                <Plus size={16} />
                Option hinzufügen
              </button>
            </div>
          </div>

          <div style={{ 
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#92400e'
          }}>
            <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
            <strong>Hinweis zur Sektion:</strong> Fragen in "Zielgebietslogik" beeinflussen den Algorithmus. 
            "Erlebnisprofil"-Fragen werden nur für die Zusammenfassung gespeichert.
          </div>

          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={newQuestion.options.length === 0}
            >
              <Plus size={16} />
              Frage erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionsEditor;
